/**
 * 🔴 **THIS MACHINE** (D83) — a random id the browser keeps, so a laptop somebody signs in from every
 * day earns a three-day session instead of a one-day one.
 *
 * Devin: *"We need these sessions to be running twenty four hours before it forces them to re sign in
 * or even three days if it's from the same machine."*
 *
 * 🔴 **IT AUTHORISES NOTHING, AND IT IDENTIFIES NOBODY.** It is not a fingerprint: nothing is measured
 * about the machine, no canvas, no fonts, no screen size. It is a uuid this browser generated for
 * itself, held in `localStorage`, and the server only ever asks one question of it — *has this person
 * signed in with this id before?* A copied id buys three days instead of one on a session the
 * PASSWORD already earned; it opens nothing on its own. Written down plainly because "device id" is
 * exactly the phrase that usually hides a tracking scheme.
 *
 * It is per-origin and per-profile, like everything in `localStorage`: a private window, a different
 * browser or a cleared site-data is simply a machine we have not seen, which costs one day.
 */
export const DEVICE_STORAGE_KEY = "pinksy:device";

let inMemory: string | null = null;

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const looksLikeId = (value: unknown): value is string => typeof value === "string" && /^[0-9a-f-]{36}$/i.test(value);

/**
 * This browser's id, minting one the first time it is asked.
 *
 * Returns null when there is nowhere to keep it — a locked-down browser, a private window with
 * storage disabled. Null is sent as no device id at all, which means a 24-hour session: the honest
 * outcome, and never a fresh id per request, which would be an unbounded row per sign-in.
 */
export function deviceId(): string | null {
  if (inMemory !== null) return inMemory;
  const store = storage();
  if (store === null) return null;
  try {
    const held = store.getItem(DEVICE_STORAGE_KEY);
    if (looksLikeId(held)) {
      inMemory = held;
      return held;
    }
    // `randomUUID` needs a secure context; a dev host on plain http has none, and a sign-in that
    // throws here would be a sign-in broken by a convenience.
    const minted = typeof crypto?.randomUUID === "function" ? crypto.randomUUID() : null;
    if (minted === null) return null;
    store.setItem(DEVICE_STORAGE_KEY, minted);
    inMemory = minted;
    return minted;
  } catch {
    return null;
  }
}

/** Tests, and "this is not my computer": forget the id, and the next sign-in lasts a day. */
export function forgetDevice(): void {
  inMemory = null;
  try {
    storage()?.removeItem(DEVICE_STORAGE_KEY);
  } catch {
    // nothing to forget
  }
}
